/* ===================================================================
   InlineCardStep -- the CARD step of the first review-request SEND, captured
   INLINE on the same screen (card-at-send inline, Steve directive 2026-07-08,
   Hopper).

   Steve's locked decision: the card is the LAST step of the send. A card-free
   owner selects recipients + previews in the send modal, taps Send, and instead
   of a redirect this panel appears OVER the send flow. It shows -- all on one
   screen -- the plan price, an EMBEDDED Stripe card
   field (Payment Element, card never leaves the page), the ROSCA disclosure +
   explicit recurring-charge consent, and ONE button: "Add card & send".

   On submit it: confirms the SetupIntent (saves the card, no charge) -> creates
   the trialing subscription -> calls onSubscribed(), which
   the parent uses to AUTO-FIRE the send the owner already built. The owner never
   re-taps Send.

   Rendered only when a publishable key is configured (getStripe() != null); the
   parent falls back to the hosted /choose-plan redirect otherwise.

   Brad still owns the visual polish pass -- this is functional-first, presentable
   but not final.
   =================================================================== */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe } from '../lib/stripe';
import {
  api,
  type BillingTier,
  type ConsentText,
  type SetupIntentResponse,
} from '../lib/api';
import { Icon, ClosePath } from './ui';
import { SmsTimelineExplainer } from './SmsTimelineExplainer';

/* ONE PRICE (Steve, 2026-08-06). The Starter/Pro segmented control is gone --
   there is nothing to choose. NOTE: the legally operative disclosure next to
   the card comes from the SERVER (consent.disclosures), never from this
   constant; this is only the at-a-glance price chip. Keep the two in sync
   anyway -- a chip that disagrees with the disclosure is how customers lose
   trust even when the disclosure is correct. */
const PLAN_INFO = {
  name: 'Rhody Reviews',
  price: '$59',
} as const;

/* The single plan identifier the API accepts. */
const PLAN_TIER: BillingTier = 'basic';

function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function InlineCardStep({
  recipientCount,
  onClose,
  onSubscribed,
}: {
  /** How many customers the owner selected -- for the button/subtext copy. */
  recipientCount: number;
  onClose: () => void;
  /** Fires after the trialing subscription is created. The parent auto-fires
      the held send. */
  onSubscribed: () => void;
}) {
  // Verbatim disclosure/consent wording + Terms links (shared with checkout).
  const consentQ = useQuery<ConsentText, Error>({
    queryKey: ['billing-consent-text'],
    queryFn: ({ signal }) => api.getConsentText(signal),
    staleTime: 5 * 60_000,
  });

  // One SetupIntent per open. staleTime Infinity so a re-render never re-creates
  // it; the panel mounts once when the owner taps Send.
  const setupQ = useQuery<SetupIntentResponse, Error>({
    queryKey: ['billing-setup-intent'],
    queryFn: () => api.createSetupIntent(),
    staleTime: Infinity,
    gcTime: 0,
    retry: 1,
  });

  const stripePromise = useMemo(() => getStripe(), []);

  // The embedded card field matches the surrounding surface. It used to switch
  // to Stripe's 'night' base when the OS was in dark mode; that is REMOVED
  // (Steve, 2026-08-06 -- outdoor high-contrast only, no dark anywhere), so the
  // light 'stripe' base is the only one and the tokens it reads are now always
  // the light set (--ink #000, --surface #fff).
  const appearance: Appearance = useMemo(() => ({
    theme: 'stripe',
    variables: {
      colorPrimary: cssVar('--accent', '#1a73e8'),
      colorText: cssVar('--ink', '#202124'),
      colorBackground: cssVar('--surface', '#ffffff'),
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '10px',
    },
  }), []);

  const options: StripeElementsOptions | null = setupQ.data?.client_secret
    ? { clientSecret: setupQ.data.client_secret, appearance }
    : null;

  return (
    <div
      className="modal-bg"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal__head">
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
            }}
          >
            One last step
          </span>
          <button className="x" onClick={onClose} aria-label="Close">
            <Icon d={ClosePath} />
          </button>
        </div>

        <div className="modal__body">
          {(consentQ.isError || setupQ.isError) && (
            <div style={{ color: 'var(--red)', fontSize: 13, padding: '8px 0' }}>
              We could not start card setup just now. Please close this and try again.
            </div>
          )}

          {(!options || !stripePromise) && !setupQ.isError ? (
            <div className="muted" style={{ padding: 16, fontSize: 13 }}>
              Loading secure card field...
            </div>
          ) : options && stripePromise ? (
            <Elements stripe={stripePromise} options={options}>
              <InlineCardForm
                recipientCount={recipientCount}
                consent={consentQ.data}
                onSubscribed={onSubscribed}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* Exported so the DEV-ONLY visual preview route (routes/DevInlineCardPreview,
   gated on import.meta.env.DEV) can render the EXACT card-step markup/styles in
   deferred Elements mode for a screenshot -- without a backend/SetupIntent.
   `previewNoop` makes the submit a pure no-op in that preview; it is never
   passed by the real InlineCardStep, so production behavior is unchanged. */
export function InlineCardForm({
  recipientCount,
  consent,
  onSubscribed,
  previewNoop = false,
}: {
  recipientCount: number;
  consent: ConsentText | undefined;
  onSubscribed: () => void;
  previewNoop?: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  // ONE PLAN -> no plan choice to make on this screen any more.
  const [locations, setLocations] = useState(0);
  const [terms, setTerms] = useState(false);
  const [smsAuthorized, setSmsAuthorized] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const subscribe = useMutation({
    mutationFn: async () => {
      if (!stripe || !elements) throw new Error('Card field is still loading.');
      // 1) Save the card (confirm the SetupIntent). No charge. redirect:if_required
      //    keeps us on-page for card (no redirect-based methods here).
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) throw new Error(error.message || 'Your card could not be saved.');
      const pm =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;
      if (!pm) throw new Error('Your card could not be saved. Please try again.');
      // 2) Create the trialing subscription with that card.
      return api.createSubscription({
        tier: PLAN_TIER,
        // Ungated: any customer can buy additional locations (Steve 2026-08-06).
        locations,
        payment_method_id: pm,
        tcpa_consent: terms,
        tcpa_consent_version: consent?.version,
        sms_authorized: smsAuthorized,
      });
    },
    onSuccess: () => {
      // The tenant is now trialing-with-card -> the gate is open. Refresh the
      // plan cache so the app un-gates, then fire the held send.
      void queryClient.invalidateQueries({ queryKey: ['billing-plan'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
      onSubscribed();
    },
    onError: (e: unknown) => {
      setErr(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    },
  });

  // Server-rendered ROSCA disclosure for THE plan. This, not the price chip
  // above, is the legally operative text shown next to the card.
  const disc = consent?.disclosures?.[PLAN_TIER];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // DEV visual preview: the button is a pure no-op (no Stripe/backend).
        if (previewNoop) return;
        setErr(null);
        if (!terms) {
          setErr('Please accept the Terms of Service and Privacy Policy to continue.');
          return;
        }
        subscribe.mutate();
      }}
    >
      {/* HERO -- the free trial + first send IS the pitch, not a checkout. */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <span
          style={{
            display: 'inline-block',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
            padding: '4px 11px',
            borderRadius: 99,
          }}
        >
          Free for 14 days
        </span>
        <h3 style={{ fontSize: 26, lineHeight: 1.12, margin: '12px 0 0' }}>
          Send your first review request
        </h3>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', margin: '6px 0 0' }}>
          Start your 14-day free trial.
        </p>
        <p className="muted" style={{ fontSize: 13, margin: '8px 0 0' }}>
          No charge today. Cancel anytime.
        </p>
      </div>

      {/* DE-EMPHASIZED price line -- just what the free trial becomes later.
          This WAS a Starter/Pro segmented control. There is one plan now, so it
          is a statement of fact rather than a decision put in front of someone
          who only came here to send their first review request.

          The legally operative amount is NOT this chip -- it is `disc.disclosure`
          below, which comes from the server. This chip must still agree with it;
          a price on screen that contradicts the disclosure destroys trust even
          when the disclosure itself is correct. */}
      <div style={{ marginBottom: 16 }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 7 }}>
          Free for 14 days, then:
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 10px',
            borderRadius: 10,
            fontSize: 13,
            border: '1.5px solid var(--accent)',
            background: 'color-mix(in srgb, var(--accent) 7%, transparent)',
            color: 'var(--ink)',
            fontWeight: 700,
          }}
        >
          <span>{PLAN_INFO.price}/4 weeks</span>
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
          Every feature included. Multiple locations? Add them below (+$25/4 weeks each).
          No charge today. After your free trial you&rsquo;re billed every 4 weeks. Cancel anytime. Your plan stays active through the end of your current paid period, then stops.
        </div>
      </div>

      {/* Additional locations -- available to EVERY customer (Steve 2026-08-06:
          "the $25 location add-on should be on every single one"). This used to
          render ONLY when the Pro tier was selected, so an ordinary customer had
          no way to buy one. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span className="muted" style={{ fontSize: 12 }}>
          Additional locations (+$25/4 weeks each)
        </span>
        <input
          className="inp"
          type="number"
          min={0}
          value={locations}
          onChange={(e) => setLocations(Math.max(0, parseInt(e.target.value || '0', 10)))}
          style={{ maxWidth: 80, height: 34 }}
        />
      </label>

      {/* Embedded Stripe card field -- card only, wallets/Link off (SetupIntent is
          card-only; wallets explicitly disabled), card never leaves the page. */}
      <div style={{ marginBottom: 14 }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Card details</div>
        <PaymentElement
          options={{
            layout: 'tabs',
            wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          }}
        />
      </div>

      {/* Fine print: full ROSCA disclosure (substance unchanged), clear-and-
          conspicuous and adjacent to the card/button, demoted to genuine fine print. */}
      {disc && (
        <p
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: 1.55,
            color: 'var(--muted)',
            background: 'var(--surface-2)',
            border: '1px solid var(--line-2)',
            borderRadius: 8,
            padding: '9px 11px',
            marginBottom: 12,
          }}
        >
          {disc.disclosure}
        </p>
      )}

      {/* REQUIRED Terms/Privacy acceptance + the explicit recurring-charge
          consent statement (the consent_line), together, before submit.
          Lightened to fine print but kept fully present. */}
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 9, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, color: 'var(--muted)' }}>
        <input
          type="checkbox"
          checked={terms}
          onChange={(e) => {
            setErr(null);
            setTerms(e.target.checked);
          }}
          style={{ marginTop: 2 }}
        />
        <span>
          {disc ? disc.consent_line + ' ' : ''}
          I have read and agree to the Rhody Reviews{' '}
          <a className="link" href={consent?.terms_url ?? 'https://rhodyreviews.com/terms'} target="_blank" rel="noreferrer">Terms of Service</a>{' '}
          and{' '}
          <a className="link" href={consent?.privacy_url ?? 'https://rhodyreviews.com/privacy'} target="_blank" rel="noreferrer">Privacy Policy</a>.
        </span>
      </label>

      {/* A2P / texting expectation-setter, right where the card is collected
          (Steve 2026-07-29). Collapsed by default so a ready buyer is never
          walled off, but present and one tap away -- and written so whoever
          onboards the client can read it aloud on the call. See the component
          header for the fact base (MODEL A: no per-client carrier wait; the
          real gate is the consumer's YES). */}
      <div style={{ marginBottom: 12 }}>
        <SmsTimelineExplainer />
      </div>

      {/* OPTIONAL, separate SMS-sending authorization (declining still subscribes). */}
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, color: 'var(--muted)' }}>
        <input
          type="checkbox"
          checked={smsAuthorized}
          onChange={(e) => setSmsAuthorized(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <span>
          Optional. I authorize Rhody Reviews to send review-request and follow-up text
          messages to my own customers who have opted in, on behalf of my business. Not
          required to subscribe.
        </span>
      </label>

      {err && (
        <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 10 }}>{err}</div>
      )}

      <button
        type="submit"
        className="btn btn--primary btn--lg"
        style={{ width: '100%' }}
        disabled={subscribe.isPending || !stripe || !elements}
      >
        {subscribe.isPending
          ? 'Starting your free trial...'
          : `Start free trial & send${recipientCount ? ` (${recipientCount})` : ''}`}
      </button>
      <p className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 8, lineHeight: 1.4 }}>
        Secured by Stripe. No charge today. Cancel anytime in one click.
      </p>
    </form>
  );
}
